import React, { useState, useEffect } from 'react';
import { Button, Container, Row, Col, ListGroup, Image, Carousel, Tab, Tabs, Modal } from 'react-bootstrap';
import { 
  CheckCircle, XCircle, Clock, MapPin, Users, User, LogIn,
  Calendar, ArrowLeft, Map as MapIcon, ChevronDown, ChevronUp, Info 
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import "./TourInfo.css";

import { supabase } from "../supabaseClient";

const TourInfo = ({ tourData }) => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0];

  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  // Состояние для раскрытия секций "Read More"
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (index) => {
    setExpandedSection(expandedSection === index ? null : index);
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setCurrentUser(session?.user ?? null);
    });
  }, []);

  const handleBooking = () => {
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }
    navigate(`/${lang}/tours/booking/${tourData.id}`);
  };


  return (
    <div className="tour-details-wrapper">
      {/* 1. ГЕРОЙ-БАННЕР */}
      <div className="tour-hero-section" style={{ backgroundImage: `url(${tourData.image})` }}>
        <div className="tour-hero-overlay">
          <Container>
            <button className="tour-back-btn" onClick={() => navigate(`/${lang}/private-tours`)}>
              <ArrowLeft size={20} />
              <span>{t('tour_info_page.back_button')}</span>
            </button>
            <h1 className="tour-main-title">{tourData.title}</h1>
          </Container>
        </div>
      </div>

      <Container className="tour-content-container py-5">
        <Row>
          <Col lg={8}>
            {/* 2. КРАТКИЕ ФАКТЫ */}
            <div className="tour-quick-stats mb-5">
              <div className="stat-item">
                <Clock size={22} />
                <div>
                  <span className="stat-label">{t('tour_info_page.duration')}</span>
                  <span className="stat-value">{tourData.duration} {tourData.duration <= 4 ? t('tour_info_page.days') : t('tour_info_page.hours')}</span>
                </div>
              </div>
              <div className="stat-item">
                <Users size={22} />
                <div>
                  <span className="stat-label">{t('tour_info_page.group_size')}</span>
                  <span className="stat-value">{t('tour_info_page.up_to')} 6 {t('tour_info_page.people')}</span>
                </div>
              </div>
              <div className="stat-item">
                <MapPin size={22} />
                <div>
                  <span className="stat-label">{t('tour_info_page.location')}</span>
                  <span className="stat-value">{t('tour_info_page.armenia')}</span>
                </div>
              </div>
            </div>

            {/* 3. КАРТА И ТАЙМЛАЙН МАРШРУТА */}
            <section className="route-visual-section mb-5">
              <h3 className="section-title mb-4">
                <MapIcon className="me-2 text-success" /> {t('tour_info_page.route_overview')}
              </h3>
              <div className="route-card p-4 shadow-sm bg-white rounded-4 border">
                <Row className="align-items-center">
                  <Col md={6} className="mb-4 mb-md-0">
                    <div className="map-wrapper">
                      <Image 
                        src={tourData.routeMap || tourData.image} 
                        fluid 
                        className="rounded-3 shadow-sm"
                        alt="Route Map"
                        loading="lazy"
                      />
                      <div className="map-badge">{t('tour_info_page.view_route')}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="simple-timeline">
                      {(tourData.sections || []).map((step, idx) => (
                        <div key={idx} className="timeline-step">
                          <div className="step-dot"></div>
                          <div className="step-info">
                            <span className="step-title">{step.header}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Col>
                </Row>
              </div>
            </section>

            {/* 4. ПОДРОБНАЯ ПРОГРАММА (READ MORE) */}
            <section className="itinerary-details-section mb-5">
              <h3 className="section-title mb-4">{t('tour_info_page.itinerary')}</h3>

              {tourData.durationUnit === "days" ? (
                (() => {
                  const days = tourData.sections.reduce((acc, section) => {
                    const day = section.day ?? 1;
                    if (!acc[day]) acc[day] = [];
                    acc[day].push(section);
                    return acc;
                  }, {});

                  const dayKeys = Object.keys(days).sort((a, b) => a - b);

                  return (
                    <Tabs
                      defaultActiveKey={dayKeys[0]}
                      id="itinerary-day-tabs"
                      className="custom-tabs mb-3"
                    >
                      {dayKeys.map((day) => (
                        <Tab
                          key={day}
                          eventKey={day}
                          title={
                            <span>
                              <Calendar size={16} className="me-1" />
                              {t('tour_info_page.day', 'Day')} {day}
                            </span>
                          }
                        >
                          <div className="custom-itinerary-list pt-2">
                            {days[day].map((section, index) => {
                              const sectionKey = `day${day}-${index}`;
                              const isExpanded = expandedSection === sectionKey;

                              return (
                                <div key={sectionKey} className={`itinerary-item ${isExpanded ? 'active' : ''}`}>
                                  <div className="itinerary-trigger" onClick={() => toggleSection(sectionKey)}>
                                    <div className="trigger-left">
                                      <span className="itinerary-number">{index + 1}</span>
                                      <h5>{section.header}</h5>
                                    </div>
                                    <div className="trigger-right">
                                      {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                    </div>
                                  </div>

                                  <div className={`itinerary-collapse ${isExpanded ? 'open' : ''}`}>
                                    <div className="itinerary-content p-4">
                                      <Row>
                                        <Col md={section.image ? 7 : 12}>
                                          <p className="itinerary-text">{section.content}</p>
                                          {section.fullContent && (
                                            <p className="itinerary-text mt-2">{section.fullContent}</p>
                                          )}
                                        </Col>
                                        {section.image && (
                                          <Col md={5}>
                                            <Carousel
                                              fade={false}
                                              slide={true}
                                              touch={true}
                                              indicators={false}
                                              controls={Array.isArray(section.image) ? section.image.length > 1 : false}
                                              interval={null}
                                              className="itinerary-carousel shadow-sm border rounded-3 overflow-hidden"
                                            >
                                              {(Array.isArray(section.image) ? section.image : [section.image]).map((imgSrc, idx) => {
                                                let optimizedSrc = imgSrc;
                                                if (imgSrc.includes('cloudinary.com')) {
                                                  optimizedSrc = imgSrc.replace('/upload/', '/upload/w_600,c_fill,g_auto,f_auto,q_auto/');
                                                }
                                                return (
                                                  <Carousel.Item key={idx}>
                                                    <Image
                                                      src={optimizedSrc}
                                                      fluid
                                                      alt={`${section.header} - ${idx + 1}`}
                                                      loading="lazy"
                                                      className="w-100 object-fit-cover"
                                                      style={{ height: '280px', willChange: 'transform' }}
                                                    />
                                                  </Carousel.Item>
                                                );
                                              })}
                                            </Carousel>
                                          </Col>
                                        )}
                                      </Row>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </Tab>
                      ))}
                    </Tabs>
                  );
                })()
              ) : (
                <div className="custom-itinerary-list">
                  {tourData.sections.map((section, index) => {
                    const isExpanded = expandedSection === index;
                    return (
                      <div key={index} className={`itinerary-item ${isExpanded ? 'active' : ''}`}>
                        <div className="itinerary-trigger" onClick={() => toggleSection(index)}>
                          <div className="trigger-left">
                            <span className="itinerary-number">{index + 1}</span>
                            <h5>{section.header}</h5>
                          </div>
                          <div className="trigger-right">
                            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                          </div>
                        </div>

                        <div className={`itinerary-collapse ${isExpanded ? 'open' : ''}`}>
                          <div className="itinerary-content p-4">
                            <Row>
                              <Col md={section.image ? 7 : 12}>
                                <p className="itinerary-text">{section.content}</p>
                                {section.fullContent && (
                                  <p className="itinerary-text mt-2">{section.fullContent}</p>
                                )}
                              </Col>
                              {section.image && (
                                <Col md={5}>
                                  <Carousel
                                    fade={false}
                                    slide={true}
                                    touch={true}
                                    indicators={false}
                                    controls={Array.isArray(section.image) ? section.image.length > 1 : false}
                                    interval={null}
                                    className="itinerary-carousel shadow-sm border rounded-3 overflow-hidden"
                                  >
                                    {(Array.isArray(section.image) ? section.image : [section.image]).map((imgSrc, idx) => {
                                      let optimizedSrc = imgSrc;
                                      if (imgSrc.includes('cloudinary.com')) {
                                        optimizedSrc = imgSrc.replace('/upload/', '/upload/w_600,c_fill,g_auto,f_auto,q_auto/');
                                      }
                                      return (
                                        <Carousel.Item key={idx}>
                                          <Image
                                            src={optimizedSrc}
                                            fluid
                                            alt={`${section.header} - ${idx + 1}`}
                                            loading="lazy"
                                            className="w-100 object-fit-cover"
                                            style={{ height: '280px', willChange: 'transform' }}
                                          />
                                        </Carousel.Item>
                                      );
                                    })}
                                  </Carousel>
                                </Col>
                              )}
                            </Row>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* 5. ВКЛЮЧЕНО / НЕ ВКЛЮЧЕНО */}
            <section className="inclusions-section bg-white p-4 rounded-4 shadow-sm border">
              <h3 className="section-title mb-4">{t('tour_info_page.whats_included')}</h3>
              <Row>
                {/* ✅ Проверяем что tourData.include существует */}
                {(tourData.include || []).map((item, idx) => (
                  <React.Fragment key={idx}>
                    <Col md={6} className="mb-3 mb-md-0">
                      <ListGroup variant="flush">
                        {/* ✅ Проверяем что featuresInclude существует */}
                        {(item.featuresInclude || []).map((feat, i) => (
                          <ListGroup.Item key={i} className="border-0 px-0 d-flex align-items-start">
                            <CheckCircle size={18} className="text-success me-2 mt-1 flex-shrink-0" />
                            <span className="inclusions-section-included">{feat}</span>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </Col>
                    <Col md={6}>
                      <ListGroup variant="flush">
                        {/* ✅ Проверяем что featuresNotInclude существует */}
                        {(item.featuresNotInclude || []).map((feat, i) => (
                          <ListGroup.Item key={i} className="border-0 px-0 d-flex align-items-start text-muted">
                            <XCircle size={18} className="text-danger me-2 mt-1 flex-shrink-0" />
                            <span className="inclusions-section-not-included">{feat}</span>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </Col>
                  </React.Fragment>
                ))}
              </Row>
            </section>
          </Col>
          
          {/* 6. ПРАВАЯ КОЛОНКА: STICKY BOOKING CARD */}
          <Col lg={4}>
            <div className="sticky-booking-card p-4 shadow-lg border-0">
              <div className="booking-card-header mb-4">
                <span className="price-label">{t('tour_info_page.starting_from')}</span>
                <h2 className="booking-price">{tourData.price}</h2>
                <div className="d-flex align-items-center">
                  <span className="me-1">4 x</span>
                  <User size={14} strokeWidth={2.5} />
                </div>
              </div>
              
              <div className="booking-features-list mb-4">
                <div className="b-feature"><Calendar size={18} className="text-success" /> {t('tour_info_page.daily_tours')}</div>
                <div className="b-feature"><CheckCircle size={18} className="text-success" /> {t('tour_info_page.free_cancel')}</div>
                <div className="b-feature"><Users size={18} className="text-success" /> {t('tour_info_page.instant_confirm')}</div>
              </div>

              <Button 
                variant="success" 
                className="w-100 py-3 fw-bold booking-main-btn mb-3"
                onClick={handleBooking}   // ← было navigate(...)
              >
                {t('tour_info_page.tour_book_button')}
              </Button>

              <div className="text-center">
                <p className="text-muted small mb-0">
                  <Info size={14} className="me-1" /> {t('tour_info_page.no_prepayment')}
                </p>
              </div>
            </div>
          </Col>

          <Col xs={12}>
            {/* 7. УСЛОВИЯ БРОНИРОВАНИЯ И ОТМЕНЫ - Using Bootstrap Tabs */}
            <section className="booking-policies-section mt-5">
              <h3 className="section-title mb-4">
                <Info className="me-2 text-success" /> {t('tour_info_page.booking_conditions')}
              </h3>
              
              <Tabs defaultActiveKey="booking" id="policy-tabs" className="custom-tabs flex-nowrap responsive-tabs">
                <Tab 
                  eventKey="booking" 
                  title={<span><Calendar size={18} className="me-2" /> {t('tour_info_page.booking_process')}</span>}
                >
                  <div className="p-4 rounded-bottom border border-top-0 bg-light">
                    <p className="text-muted mb-0">
                      {t('tour_info_page.booking_text', 'Бронирование подтверждается мгновенно. Оплата производится наличными гиду или картой в день тура.')}
                    </p>
                  </div>
                </Tab>
                <Tab 
                  eventKey="cancellation" 
                  title={<span><XCircle size={18} className="me-2" /> {t('tour_info_page.cancellation_policy')}</span>}
                >
                  <div className="p-4 rounded-bottom border border-top-0 bg-light">
                    <p className="text-muted mb-0">
                      {t('tour_info_page.cancel_text', 'Бесплатная отмена за 24 часа. При отмене менее чем за сутки удерживается 20% стоимости.')}
                    </p>
                  </div>
                </Tab>
              </Tabs>
            </section>

            {/* БЛОК: РЕКОМЕНДАЦИИ - Horizontal scroll on Mobile */}
            <section className="you-might-like-section mt-5 pt-4">
              <h3 className="section-title mb-4">{t('tour_info_page.you_might_like', 'You might also like...')}</h3>
              
              <div className="recommendations-scroll-container">
                <Row className="flex-nowrap g-3 pb-3 m-0">
                  {(tourData.relatedTours || []).map((rel) => (
                    <Col key={rel.id} className="recommendation-item">
                      <div 
                        className="related-tour-mini-card shadow-sm border-0 rounded-4 overflow-hidden d-flex flex-column"
                        onClick={() => {
                          navigate(`${lang}/private-tours/${rel.id}`);
                          window.scrollTo(0, 0); 
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        {/* Fixed height image container */}
                        <div className="rel-img-container">
                          <img src={rel.image} alt={rel.title} className="object-fit-cover" />
                        </div>
                        
                        {/* Flex-grow ensures the body takes up all remaining space */}
                        <div className="p-3 bg-white d-flex flex-column flex-grow-1 related-tour-mini-card-title">
                          <h6 className="mb-2 fw-bold text-dark multi-line-truncate">
                            {rel.title}
                          </h6>
                          <div className="mt-auto">
                            <span className="text-success fw-bold">{rel.price}</span>
                          </div>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            </section>
          </Col>
        </Row>
      </Container>

      <Modal
        show={showLoginModal}
        onHide={() => setShowLoginModal(false)}
        centered
        className="auth-alert-modal"
      >
        <Modal.Body className="p-4 text-center">
          <div className="auth-alert-icon mb-3">
            <LogIn size={32} strokeWidth={2.5} />
          </div>
          <h4 className="fw-bold mb-2">
            {t('tour_info_page.login_required_title', 'Authentication Required')}
          </h4>
          <p className="text-muted mb-4">
            {t('tour_info_page.please_login', 'Please login to book this tour.')}
          </p>
          <div className="d-grid gap-2">
            <Link to={`/${lang}/login`}>
              <Button variant="success" className="py-2 fw-bold w-100">
                {t('auth_page.btn_login', 'Login')}
              </Button>
            </Link>
            <Button
              variant="light"
              className="py-2 text-muted"
              onClick={() => setShowLoginModal(false)}
            >
              {t('auth_page.cancel', 'Maybe later')}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default TourInfo;